import React from 'react';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import ReactHtmlParser from 'react-html-parser';
import { JoykitIcon } from '@components/JoykitIcon';
import { EASING, INITIAL_HEIGHT, PLAY_SPEED, useBlogCarouselV2Controller } from './BlogCarouselV2.controller';
import {
  StyledText,
  CarouselWrapper,
  StyledCarousel,
  CarouselItem,
  StyledTextWrapper,
  ImageContainer,
  ImageCTALink,
  StyledEyeBrow,
  StyledTitle,
  StyledContent,
  PreviousSlideButton,
  NextSlideButton,
} from './BlogCarouselV2.styles';
import { snakeToCamelTrimed } from '@utils/strings.util';

export interface BlogPropsV2
  extends Readonly<{
    title: string;
    excerpt: string;
    mediaLink: string;
    link: string;
    categories: {
      id: number;
      name: string;
      link: string;
    }[];
  }> {}

interface BlogCarouselV2Props
  extends Readonly<{
    sectionId: string;
    title: string;
    blogs: ReadonlyArray<BlogPropsV2>;
  }> {}

export const BlogCarouselV2: React.FC<BlogCarouselV2Props> = ({ sectionId, title, blogs }) => {
  const { slideIndex, slidesToShow, setSlideIndex, handlePreviousOrNextSlide } = useBlogCarouselV2Controller(blogs.length);
  return (
    <Flex id={sectionId} paddingTop={[7, 88]} paddingBottom={8} backgroundColor="yellow1" alignItems="center" flexDirection="column">
      <StyledText color="mono1" variant="calloutHed40" tagName="h3">
        {title}
      </StyledText>
      <Box paddingTop={[7, 9]} paddingBottom={[0, 88]} css={{ width: '100%' }}>
        <CarouselWrapper>
          {blogs.length > slidesToShow && (
            <PreviousSlideButton
              aria-label="Previous Slide Button"
              onClick={() => handlePreviousOrNextSlide('prev')}
              id={`prev${sectionId}`}
            >
              <JoykitIcon name="backChevron" color="mono1" size={24} />
            </PreviousSlideButton>
          )}
          <StyledCarousel
            easing={EASING}
            slideIndex={slideIndex}
            withoutControls={true}
            initialSlideHeight={INITIAL_HEIGHT}
            slidesToShow={slidesToShow}
            slidesToScroll={slidesToShow}
            afterSlide={(params) => setSlideIndex(params)}
            speed={PLAY_SPEED}
            renderBottomCenterControls={() => null}
          >
            {blogs.map((blog, index) => (
              <CarouselItem key={index + blog.title} flexDirection="column">
                <ImageContainer>
                  <ImageCTALink
                    id={`blog-thumbnail-${sectionId}-${blog.title}`}
                    href={blog.link}
                    backgroundImage={blog.mediaLink}
                    data-element-id={`${sectionId}_Thumbnail`}
                    aria-label={blog.title}
                    aria-labelledby={blog.title}
                  />
                </ImageContainer>
                <StyledTextWrapper flexDirection="column" padding={7} backgroundColor="white">
                  <Flex>
                    {blog.categories.map((category, idx) => (
                      <StyledEyeBrow
                        key={`blogTitle${sectionId}${snakeToCamelTrimed(blog.title)}${snakeToCamelTrimed(category.name)}`}
                        id={`blogTitle${sectionId}${snakeToCamelTrimed(blog.title)}${snakeToCamelTrimed(category.name)}`}
                        typography="sectionEyeBrow20"
                        href={category.link}
                        hasSpan={false}
                        isLast={blog.categories.length === idx + 1}
                        data-element-id={`${sectionId}_Category`}
                      >
                        {category.name}
                      </StyledEyeBrow>
                    ))}
                  </Flex>
                  <StyledTitle
                    id={`blogTitle${sectionId}${snakeToCamelTrimed(blog.title)}`}
                    typography="austinNewsDek24"
                    hasSpan={false}
                    href={blog.link}
                    data-element-id={`${sectionId}_Title`}
                  >
                    {ReactHtmlParser(blog.title)}
                  </StyledTitle>
                  <StyledContent color="neutra5" variant="featureCopy17" tagName="span">
                    {ReactHtmlParser(blog.excerpt)}
                  </StyledContent>
                </StyledTextWrapper>
              </CarouselItem>
            ))}
          </StyledCarousel>
          {blogs.length > slidesToShow && (
            <NextSlideButton aria-label="Next Slide Button" onClick={() => handlePreviousOrNextSlide('next')} id={`next${sectionId}`}>
              <JoykitIcon name="forwardChevron" color="mono1" size={24} />
            </NextSlideButton>
          )}
        </CarouselWrapper>
      </Box>
    </Flex>
  );
};
